import { useEffect, useState } from "react";
import { useTheme, Box, Typography } from "@mui/material";
import { ResponsiveChoropleth } from "@nivo/geo";
import { geoFeatures } from "../data/mockGeoFeatures";
import { tokens } from "../theme";
import MarqueService from "../api/services/MarqueService";

const GeographyChart = ({ isDashboard = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [geoData, setGeoData] = useState([]);
  const [marquesByCountry, setMarquesByCountry] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const countryMapping = {
    France: "FRA",
    UE: "UE",
    UK: "GBR",
    "Royaume-Uni": "GBR",
    USA: "USA",
    "États-Unis": "USA",
    Canada: "CAN",
    Suisse: "CHE",
  };

  const euCountries = ["DEU", "ITA", "ESP", "POL"];

  const countryColors = {
    FRA: "#2563eb",
    GBR: "#ef4444",
    USA: "#16a34a",
    CAN: "#d946ef",
    CHE: "#9333ea",
    DEU: "#f97316",
    ITA: "#22c55e",
    ESP: "#eab308",
    POL: "#475569",
  };

  const countryFlags = {
    FRA: "🇫🇷",
    GBR: "🇬🇧",
    USA: "🇺🇸",
    CAN: "🇨🇦",
    CHE: "🇨🇭",
    DEU: "🇩🇪",
    ITA: "🇮🇹",
    ESP: "🇪🇸",
    POL: "🇵🇱",
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userRole = localStorage.getItem("user_role");

    const fetchMarques = async () => {
      if (!token || !userRole) {
        setError("Utilisateur non authentifié.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const res = await MarqueService.getMarques(userRole, token);

        const marquesData = Array.isArray(res) ? res : res.resultats || [];

        if (!Array.isArray(marquesData)) {
          console.error("Les données des marques ne sont pas un tableau :", marquesData);
          setError("Données des marques mal formatées.");
          setLoading(false);
          return;
        }

        const countByCountry = {};
        const marquesByCountryTemp = {};
        marquesData.forEach((marque) => {
          const zones = Array.isArray(marque.zones)
            ? marque.zones.map((zone) => (typeof zone === "string" ? zone : zone.zone))
            : [marque.zone_de_protection || "N/A"];

          zones.forEach((zone) => {
            const countryCode = countryMapping[zone] || "N/A";
            if (countryCode === "UE") {
              euCountries.forEach((euCountry) => {
                countByCountry[euCountry] = (countByCountry[euCountry] || 0) + 1;
                marquesByCountryTemp[euCountry] = marquesByCountryTemp[euCountry] || [];
                marquesByCountryTemp[euCountry].push(marque.nom || "Marque sans nom");
              });
            } else if (countryCode !== "N/A") {
              countByCountry[countryCode] = (countByCountry[countryCode] || 0) + 1;
              marquesByCountryTemp[countryCode] = marquesByCountryTemp[countryCode] || [];
              marquesByCountryTemp[countryCode].push(marque.nom || "Marque sans nom");
            }
          });
        });

        const allCountryCodes = ["FRA", "CHE", "GBR", "USA", "CAN", "DEU", "ITA", "ESP", "POL"];
        const formattedData = allCountryCodes.map((countryCode) => ({
          id: countryCode,
          value: countByCountry[countryCode] || 0,
          color: countryColors[countryCode] || "#d1d5db",
        }));

        setGeoData(formattedData);
        setMarquesByCountry(marquesByCountryTemp);
        setError("");
      } catch (error) {
        console.error("Erreur lors du chargement des marques :", error);
        setError(error.response?.data?.message || "Erreur lors de la récupération des marques.");
      } finally {
        setLoading(false);
      }
    };

    fetchMarques();
  }, []);

  const maxValue = Math.max(...geoData.map((d) => d.value || 0), 1);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <Typography color={colors.grey[100]} fontSize="18px" fontWeight="600">
          Chargement de la carte...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <Typography color="error" fontSize="18px" fontWeight="600">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          height: "300px",
          width: "100%",
          background: theme.palette.mode === "dark" ? "#1e293b" : "#ffffff",
          borderRadius: "16px",
          boxShadow: "0px 4px 15px rgba(0,0,0,0.15)",
          p: 2,
          transition: "all 0.3s ease",
          "&:hover": { boxShadow: "0px 6px 20px rgba(0,0,0,0.25)" },
        }}
      >
        <ResponsiveChoropleth
          data={geoData}
          features={geoFeatures.features}
          theme={{
            background: "transparent",
            axis: {
              domain: { line: { stroke: colors.grey[100] } },
              legend: { text: { fill: colors.grey[100], fontSize: 12 } },
              ticks: {
                line: { stroke: colors.grey[100], strokeWidth: 1 },
                text: { fill: colors.grey[100] },
              },
            },
            legends: { text: { fill: colors.grey[100], fontSize: 13 } },
          }}
          margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
          domain={[0, maxValue]}
          unknownColor="#d1d5db"
          label="properties.name"
          valueFormat=".0f"
          projectionType="mercator"
          projectionScale={isDashboard ? 110 : 170}
          projectionTranslation={[0.5, 0.65]}
          projectionRotation={[0, 0, 0]}
          enableGraticule={false}
          borderWidth={1.2}
          borderColor="#ffffff"
          colors={geoData.map((d) => d.color)}
          tooltip={({ feature }) => {
            const data = geoData.find((d) => d.id === feature.id);
            const countryMarques = marquesByCountry[feature.id] || [];
            return (
              <Box
                sx={{
                  backgroundColor: theme.palette.mode === "dark" ? "#374151" : "#f3f4f6",
                  borderRadius: "10px",
                  padding: "12px 16px",
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.25)",
                  border: "1px solid #9ca3af",
                  minWidth: "180px",
                }}
              >
                <Box display="flex" flexDirection="column" gap={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="bold">
                      {countryFlags[feature.id] || "🌍"} {feature.properties.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={theme.palette.mode === "dark" ? "#93c5fd" : "#2563eb"}
                    >
                      {data?.value || 0} marque{data?.value !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                  {countryMarques.length > 0 ? (
                    <Box>
                      <Typography variant="body2" fontWeight="medium" color={colors.grey[100]}>
                        Marques :
                      </Typography>
                      <Box
                        component="ul"
                        sx={{ pl: 2, m: 0, maxHeight: "100px", overflowY: "auto" }}
                      >
                        {countryMarques.map((marque, index) => (
                          <Typography
                            key={index}
                            component="li"
                            variant="body2"
                            color={colors.grey[200]}
                            sx={{ fontSize: "12px" }}
                          >
                            {marque}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Typography
                      variant="body2"
                      color={colors.grey[200]}
                      sx={{ fontStyle: "italic" }}
                    >
                      Aucune marque enregistrée
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          }}
          legends={
            !isDashboard
              ? [
                {
                  anchor: "bottom-left",
                  direction: "column",
                  translateX: 30,
                  translateY: -60,
                  itemWidth: 100,
                  itemHeight: 22,
                  itemDirection: "left-to-right",
                  itemTextColor: colors.grey[100],
                  itemOpacity: 0.9,
                  symbolSize: 20,
                  symbolShape: "circle",
                  effects: [
                    {
                      on: "hover",
                      style: {
                        itemTextColor: theme.palette.mode === "dark" ? "#ffffff" : "#0f172a",
                        itemOpacity: 1,
                      },
                    },
                  ],
                },
              ]
              : undefined
          }
        />
      </Box>
    </Box>
  );
};

export default GeographyChart;